import React from 'react';
import { motion } from 'framer-motion';
import { Play } from 'lucide-react';
import { type ModuleData, getModuleDisplayInfo, getModuleColors, getModuleIcon } from '../../content';
import { GroupId } from '../../config/systemConfig';
import { getGrowthStage } from '../../mastery';
import { DjayAvatar } from '../../mascot';
import type { DjayVariant } from '../../mascot';
interface AdventureCTAProps {
  module: ModuleData;
  selectedGroup: GroupId;
  onStart: () => void;
  getModuleMasteryForGroup: (moduleId: string) => number;
  djayVariant?: DjayVariant;
}
export function AdventureCTA({
  module,
  selectedGroup,
  onStart,
  getModuleMasteryForGroup,
  djayVariant = 'default'
}: AdventureCTAProps) {
  const displayInfo = getModuleDisplayInfo(module.id, selectedGroup);
  const title = displayInfo?.title || module.title;
  const mastery = getModuleMasteryForGroup(module.id);
  const growthStage = getGrowthStage(mastery);
  const colors = getModuleColors(module.color);
  const Icon = getModuleIcon(module.icon);
  let message = 'Ga verder waar je gebleven was';
  if (mastery < 20) message = 'Je bent goed begonnen!';else if (mastery < 50) message = 'Je bent lekker op weg!';else if (mastery < 80) message = 'Nog even doorzetten!';else message = 'Bijna klaar met dit vak!';
  return <motion.div whileHover={{
    y: -4,
    scale: 1.01
  }} whileTap={{
    scale: 0.98
  }} className="relative cursor-pointer group" onClick={onStart} data-id="element-997">
      {/* Main Banner */}
      <div className={`relative overflow-hidden rounded-[2rem] bg-gradient-to-r ${colors.from} ${colors.to} p-1 shadow-lg`} data-id="element-998">
        <div className="bg-white/10 backdrop-blur-sm rounded-[1.8rem] p-4 sm:p-6 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 relative z-10" data-id="element-999">
          {/* Left: Icon & Progress */}
          <div className="relative flex-shrink-0" data-id="element-1000">
            <div className={`w-20 h-20 bg-white rounded-3xl flex items-center justify-center shadow-md transform -rotate-6 group-hover:rotate-0 transition-transform duration-300`} data-id="element-1001">
              <Icon className={`w-10 h-10 ${colors.text}`} strokeWidth={2.5} data-id="element-1002" />
            </div>
            <div className="absolute -bottom-3 -right-3 bg-white px-2 py-1 rounded-full shadow-sm border-2 border-slate-50 flex items-center gap-1" data-id="element-1003">
              <span className="text-sm" data-id="element-1004">{growthStage.emoji}</span>
              <span className="text-xs font-black text-slate-700" data-id="element-1005">
                {Math.round(mastery)}%
              </span>
            </div>
          </div>

          {/* Center: Text Content */}
          <div className="flex-1 text-center sm:text-left text-white" data-id="element-1006">
            <div className="text-white/80 text-sm font-bold uppercase tracking-wider mb-1" data-id="element-1007">
              Jouw Avontuur
            </div>
            <h3 className="text-2xl sm:text-3xl font-black tracking-tight mb-1 drop-shadow-sm" data-id="element-1008">
              {title}
            </h3>
            <p className="text-white/90 font-medium text-lg" data-id="element-1009">{message}</p>
          </div>

          {/* Right: Play Button */}
          <div className="flex-shrink-0 mt-4 sm:mt-0" data-id="element-1010">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300" data-id="element-1011">
              <Play className={`w-8 h-8 ${colors.text} ml-1`} fill="currentColor" data-id="element-1012" />
            </div>
          </div>
        </div>

        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" data-id="element-1013" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-black/10 rounded-full blur-2xl translate-y-1/4 -translate-x-1/4" data-id="element-1014" />
      </div>

      {/* Djay Peeking */}
      <div className="absolute -bottom-2 -left-4 sm:-left-6 transform -rotate-12 pointer-events-none z-20" data-id="element-1015">
        <DjayAvatar size="sm" emotion="pointing" variant={djayVariant} animate data-id="element-1016" />
      </div>
    </motion.div>;
}